import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { list, getImages, searchProductsByImage } from "../auth/apiProduct";
import { getCategoriesByName } from "../auth/apiCategory";
import ShowimagesLoading from "./showImagesLoading";
import useDebouncedValue from "./hooks/useDebouncedValue";
import { getProductPrimaryImage } from "../auth/elakay-utils";
import '../Layout/Header.css';

let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["search"]?.[key] || fallback;

// The header's phone layout (elakay_index.css, max-width: 700px).
const MOBILE_HEADER_QUERY = "(max-width: 700px)";
const useMobileHeader = () => {
    const read = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(MOBILE_HEADER_QUERY).matches;
    const [mobile, setMobile] = useState(read);
    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
        const media = window.matchMedia(MOBILE_HEADER_QUERY);
        const update = () => setMobile(media.matches);
        update();
        if (media.addEventListener) media.addEventListener("change", update);
        else media.addListener(update);
        return () => {
            if (media.removeEventListener) media.removeEventListener("change", update);
            else media.removeListener(update);
        };
    }, []);
    return mobile;
};

const Search = (props) => {
    const [searchShow, setSearchShow] = useState(false);
    const navigate = useNavigate();
    const [searchVar, setSearchVar] = useState("");
    const [search, setSearch] = useState([]);
    const [categorySearch, setCategorySearch] = useState([]);
    const [error, setError] = useState("");
    const [imageSearching, setImageSearching] = useState(false);
    const imageInputRef = useRef(null);
    let limit = 10;
    let skip = 0;

    // collapseOnMobile: on a phone the search is a [camera | search] pill that
    // opens into the input when touched and folds back when left empty -- the
    // design and animation of the shop header's search (ExpandingSearch).
    const { infoSource, collapseOnMobile = false, onExpandedChange } = props;
    __setElakayInfoSource(infoSource);
    const inputRef = useRef(null);
    const [expanded, setExpanded] = useState(false);
    const collapsible = useMobileHeader() && collapseOnMobile;
    const collapsed = collapsible && !expanded;

    useEffect(() => {
        onExpandedChange?.(Boolean(collapsible && expanded));
    }, [collapsible, expanded, onExpandedChange]);

    useEffect(() => () => onExpandedChange?.(false), [onExpandedChange]);

    const openSearch = (event) => {
        // Open: the button submits the search, as it always did.
        if (!collapsed) return;
        event.preventDefault();
        setExpanded(true);
        inputRef.current?.focus();
    };

    const closeWhenLeftEmpty = (event) => {
        if (!collapsible || event.currentTarget.contains(event.relatedTarget) || inputRef.current?.value) return;
        setExpanded(false);
    };

    const searchData = (search) => {
        if (search) {
            list(skip, limit, search).then(
                response => {
                    if (response.error) {
                        setError(response.error);
                    } else {
                        setSearch(response);
                    }
                }
            );
            getCategoriesByName(skip, limit, search).then(
                response => {
                    setCategorySearch(Array.isArray(response) ? response : []);
                }
            );
        }
    };

    // Debounced: the two API calls in searchData() used to fire on every
    // keystroke. Now they only fire ~300ms after the user stops typing.
    const debouncedSearchVar = useDebouncedValue(searchVar, 300);
    useEffect(() => {
        if (debouncedSearchVar) searchData(debouncedSearchVar);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchVar]);

    const handleChange = name => event => {
        let val = event.target.value;
        setError(false);

        if (val?.length === 0) {
            searchData("undefini");
            setCategorySearch([]);
            setSearchShow(false);
            setSearchVar("");
        } else {
            setSearchShow(true);
            setSearchVar(val);
        }
    };

    const showError = () => (
        <div
            className="alert alert-danger search-error"
            style={{ display: error ? "" : "none" }}
        >
            {error}
        </div>
    );

    const directionSearch = (s, i) => {
        setSearchShow(false);
        window.location.href = `/search?ref=${s}&ci=${i}&Submit=ok`;
    };

    const handleImageSearchClick = () => {
        imageInputRef.current?.click();
    };

    const handleImageSearchChange = (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;

        setImageSearching(true);
        setSearchShow(false);
        const minSimilarity = infoSource?.allSettings?.imageSearch?.minSimilarity;
        searchProductsByImage(file, undefined, minSimilarity).then((response) => {
            setImageSearching(false);
            if (response?.error || !response?.imageQuery) {
                setError(response?.error || __elakayText("t002", "Image search failed"));
                return;
            }
            window.location.href = `/search?imgQuery=${encodeURIComponent(response.imageQuery)}&Submit=ok`;
        });
    };

    const loadingCategoyName = (search) => {
        return (
            <div className="card loadind_search modern-search-results">
                {categorySearch?.length > 0 && (
                    <div className="search-category-group">
                        {categorySearch.map((cat, i) => (
                            <Link
                                key={`cat-${cat._id || i}`}
                                onClick={() => directionSearch(searchVar, cat._id)}
                                className="search-result-item search-category-item"
                            >
                                <span className="search-item-name">
                                    {infoSource.getCategoryNames(cat, true).name.slice(0, -1).join(', ')}
                                </span>
                            </Link>
                        ))}
                    </div>
                )}
                {search.data && search.data?.map((product, i) => (
                    <Link 
                        key={i} 
                        onClick={() => directionSearch(searchVar, product.categoriesID[0].catID)}
                        className="search-result-item"
                    >
                        <div className="search-item-content">
                            <div className="search-item-text">
                                <span className="search-item-name">
                                    {product.name.substring(0, 50)}
                                    {product.name.length > 50 ? '...' : ''}
                                </span>
                            </div>
                            <div className="search-item-image">
                                <ShowimagesLoading 
                                    className="rounded search-thumb"
                                    src={getProductPrimaryImage(product)}
                                    alt={__elakayText("t001", "search images")}
                                    style={{ height: "45px", width: "45px", objectFit: "cover" }}
                                /> 
                            </div>
                        </div>
                    </Link>
                ))}
                {showError()}
            </div>
        );
    };

    const searchForm = () => (
        <div className="search-container">
            <form action={"/search"}>
                <div
                    className={`elakay_search modern-search${collapsible ? " elakay_search--collapsible" : ""}${collapsible && expanded ? " is-open" : ""}`}
                    onBlur={closeWhenLeftEmpty}
                >
                    <input
                        ref={inputRef}
                        id="txtSearch"
                        className="elakay_input_search"
                        onChange={handleChange("search")}
                        onFocus={() => { if (collapsible) setExpanded(true); }}
                        type="search"
                        placeholder={infoSource.language(infoSource, "headNenu", "searchProd")}
                        name="ref"
                        autoComplete="off"
                    />
                    <button
                        type="button"
                        className="elakay_button_image_search"
                        title={__elakayText("t003", "Search by image")}
                        onClick={handleImageSearchClick}
                        disabled={imageSearching}
                    >
                        {imageSearching ? (
                            <span className="image-search-spinner" />
                        ) : (
                            <svg width="18px" height="18px" viewBox="0 0 24 24">
                                <path
                                    fill="currentColor"
                                    d="M9 3 7.17 5H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-3.17L15 3H9m3 13a4 4 0 1 1 0-8 4 4 0 0 1 0 8Z"
                                />
                            </svg>
                        )}
                    </button>
                    <button
                        className="elakay_button_search"
                        name="Submit"
                        value="ok"
                        type="submit"
                        aria-label={infoSource.language(infoSource, "headNenu", "searchProd")}
                        aria-expanded={collapsible ? expanded : undefined}
                        onClick={openSearch}
                    >
                        <svg width="18px" height="18px" viewBox="0 0 20 20">
                            <path
                                fill="currentColor"
                                d="M11.618 9.897l4.224 4.212c.092.09.1.23.02.312l-1.464 1.46c-.08.08-.222.072-.314-.02L9.868 11.66M6.486 10.9c-2.42 0-4.38-1.955-4.38-4.367 0-2.413 1.96-4.37 4.38-4.37s4.38 1.957 4.38 4.37c0 2.412-1.96 4.368-4.38 4.368m0-10.834C2.904.066 0 2.96 0 6.533 0 10.105 2.904 13 6.486 13s6.487-2.895 6.487-6.467c0-3.572-2.905-6.467-6.487-6.467"
                            />
                        </svg>
                    </button>
                    <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleImageSearchChange}
                        style={{ display: "none" }}
                    />
                </div>
                {searchShow && loadingCategoyName(search)}
            </form>

            <style>{`
                .search-container {
                    position: relative;
                }

                .modern-search-results {
                    margin-top: 8px;
                    max-height: 400px;
                    overflow-y: auto;
                    border: 1px solid #e5e7eb;
                }

                .modern-search-results::-webkit-scrollbar {
                    width: 6px;
                }

                .modern-search-results::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 3px;
                }

                .modern-search-results::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 3px;
                }

                .modern-search-results::-webkit-scrollbar-thumb:hover {
                    background: #94a3b8;
                }

                .search-result-item {
                    display: block;
                    padding: 12px 16px;
                    border-bottom: 1px solid #f1f5f9;
                    transition: all 0.2s ease;
                    text-decoration: none;
                    color: inherit;
                }

                .search-result-item:last-child {
                    border-bottom: none;
                }

                .search-result-item:hover {
                    background: linear-gradient(90deg, #f0f9ff 0%, #ffffff 100%);
                    transform: translateX(4px);
                }

                .search-item-content {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    gap: 12px;
                }

                .search-item-text {
                    flex: 1;
                }

                .search-item-name {
                    font-size: 14px;
                    color: #1f2937;
                    font-weight: 500;
                    line-height: 1.4;
                }

                .search-item-image {
                    flex-shrink: 0;
                }

                .search-thumb {
                    border: 2px solid #e5e7eb;
                    transition: all 0.3s ease;
                }

                .search-result-item:hover .search-thumb {
                    border-color: #007399;
                    transform: scale(1.1);
                }

                .search-error {
                    margin: 8px 16px;
                    padding: 8px 12px;
                    border-radius: 6px;
                    font-size: 14px;
                }

                .search-category-group {
                    border-bottom: 1px solid #e5e7eb;
                    background: #f8fafc;
                }

                .search-category-item {
                    padding: 8px 16px;
                }

                .search-category-item .search-item-name {
                    color: #007399;
                    font-weight: 600;
                    font-size: 13px;
                    text-transform: uppercase;
                    letter-spacing: 0.3px;
                }

                .elakay_button_image_search {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: transparent;
                    border: none;
                    border-radius: 0;
                    color: #64748b;
                    padding: 11px 10px;
                    min-width: 38px;
                    cursor: pointer;
                    box-sizing: border-box;
                    position: relative;
                    z-index: 1;
                    transition: color 0.15s ease;
                }

                .elakay_button_image_search:hover {
                    color: #007399;
                }

                .elakay_button_image_search:disabled {
                    cursor: not-allowed;
                    opacity: 0.6;
                }

                .image-search-spinner {
                    display: inline-block;
                    width: 14px;
                    height: 14px;
                    border: 2px solid rgba(0, 115, 153, 0.3);
                    border-top-color: #007399;
                    border-radius: 50%;
                    animation: image-search-spin 0.7s linear infinite;
                }

                @keyframes image-search-spin {
                    to { transform: rotate(360deg); }
                }

                @media (max-width: 768px) {
                    .search-result-item {
                        padding: 10px 12px;
                    }

                    .search-item-name {
                        font-size: 13px;
                    }

                    .search-item-image img {
                        height: 40px !important;
                        width: 40px !important;
                    }
                }
            `}</style>
        </div>
    );

    return (
        <div>
            {searchForm()}
        </div>
    );
};

export default Search;
